import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Cloud,
  CloudOff,
  Compass,
  FolderOpen,
  HardDrive,
  Plus,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../auth/store';
import { CloudStorageAdapter } from '../storage/cloud';
import { useStorage } from '../storage';
import type { ProjectSummary } from '../storage/types';
import { LocalFsStorageAdapter } from '../storage/local-fs';
import { convertMermaid } from '@archer/shared';

const PROJECT_TEMPLATES: Array<{ label: string; code: string }> = [
  {
    label: '3-tier web app (C4)',
    code: `c4Container
      Person(user, "User")
      System_Boundary(b1, "Web App") {
        Container(web, "Web SPA", "React", "Customer UI")
        Container(api, "API", "Node", "Business logic")
        ContainerDb(db, "Database", "PostgreSQL", "Stores data")
      }
      Rel(user, web, "Uses")
      Rel(web, api, "Calls")
      Rel(api, db, "Reads/writes")`,
  },
  {
    label: 'OAuth2 login flow',
    code: `sequenceDiagram
      participant U as User
      participant B as Browser
      participant A as App
      participant S as Auth Server
      U->>B: click Login
      B->>A: /login
      A->>S: redirect /authorize
      S->>U: login + consent
      U->>S: grant
      S->>B: redirect ?code=...
      B->>A: /callback?code=...
      A->>S: token + code -> access token
      A->>B: session cookie`,
  },
  {
    label: 'Order state machine',
    code: `stateDiagram-v2
      [*] --> Created
      Created --> Paid
      Paid --> Fulfilled
      Fulfilled --> Shipped
      Shipped --> Delivered
      Delivered --> [*]
      Paid --> Cancelled
      Cancelled --> [*]
      Delivered --> Refunded
      Refunded --> [*]`,
  },
];

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { user, api, logout } = useAuth();
  const cloud = api ? new CloudStorageAdapter(api) : null;
  const browser = useStorage((s) => s.browserAdapter);
  const localAdapter = useStorage((s) => s.localAdapter);
  const setLocalAdapter = useStorage((s) => s.setLocalAdapter);
  const pushRecent = useStorage((s) => s.pushRecent);

  const [cloudList, setCloudList] = useState<ProjectSummary[]>([]);
  const [browserList, setBrowserList] = useState<ProjectSummary[]>([]);
  const [localList, setLocalList] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const next: typeof cloudList = cloud ? await cloud.list() : [];
      setCloudList(next);
      setBrowserList(await browser.list());
      if (localAdapter) setLocalList(await localAdapter.list());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, localAdapter]);

  const createIn = async (kind: 'cloud' | 'browser' | 'local', templateCode?: string) => {
    const id = `${Date.now().toString(36)}${Math.floor(Math.random() * 1e8).toString(36)}`;
    const name = `Untitled (${new Date().toLocaleString()})`;
    const elements = templateCode ? (await convertMermaid(templateCode)).elements : [];
    const file = {
      schemaVersion: 1 as const,
      kind: 'omniapps.archer.project' as const,
      id,
      name,
      elements,
      mermaidSources: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    if (kind === 'cloud') {
      if (!cloud) throw new Error('not signed in');
      const { project } = await cloud.api.createProject(name, file);
      pushRecent({ kind: 'cloud', storageRef: project.id, name, updatedAt: file.updatedAt });
      navigate(`/projects/${project.id}`);
      return;
    }
    if (kind === 'local') {
      if (!localAdapter) throw new Error('no folder selected');
      const ref = LocalFsStorageAdapter.fileNameFor(id, name);
      await localAdapter.save(ref, file);
      pushRecent({ kind: 'local', storageRef: ref, name, updatedAt: file.updatedAt });
      navigate(`/projects/${ref}?kind=local`);
      return;
    }
    await browser.save(id, file);
    pushRecent({ kind: 'browser', storageRef: id, name, updatedAt: file.updatedAt });
    navigate(`/projects/${id}?kind=browser`);
  };

  const openProject = (s: ProjectSummary) => {
    const q = s.storageKind === 'cloud' ? '' : `?kind=${s.storageKind}`;
    navigate(`/projects/${encodeURIComponent(s.storageRef)}${q}`);
  };

  const remove = async (s: ProjectSummary) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    const adapter =
      s.storageKind === 'cloud'
        ? cloud
        : s.storageKind === 'local'
          ? localAdapter
          : browser;
    if (!adapter) return;
    await adapter.remove(s.storageRef);
    await refresh();
  };

  const pickLocalFolder = async () => {
    if (!LocalFsStorageAdapter.isSupported()) {
      alert('Local folder mode requires a Chromium-based browser (Chrome, Edge, Brave, Arc).');
      return;
    }
    try {
      const a = await LocalFsStorageAdapter.pick();
      setLocalAdapter(a);
    } catch (err) {
      console.error(err);
    }
  };

  const Section = ({ title, icon, items, kind, emptyHint }: {
    title: string;
    icon: React.ReactNode;
    items: ProjectSummary[];
    kind: 'cloud' | 'browser' | 'local';
    emptyHint?: string;
  }) => (
    <section className="mb-8">
      <div className="mb-3 flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">{title}</h2>
        <span className="text-xs text-slate-500">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">{emptyHint ?? 'No projects yet.'}</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <div key={`${p.storageKind}:${p.storageRef}`} className="card group">
              <div className="flex items-start justify-between gap-2">
                <button
                  className="flex-1 truncate text-left text-sm font-medium text-slate-100 hover:text-archer-300"
                  onClick={() => openProject(p)}
                >
                  {p.name}
                </button>
                <button className="tool-btn opacity-0 group-hover:opacity-100" onClick={() => void remove(p)}>
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </button>
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Updated {new Date(p.updatedAt).toLocaleString()}
              </div>
              {p.role && p.role !== 'owner' && (
                <span className="mt-2 inline-block rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-300">
                  {p.role}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button className="btn-primary" onClick={() => void createIn(kind)}>
          <Plus className="h-4 w-4" />
          New project
        </button>
        {PROJECT_TEMPLATES.map((t) => (
          <button key={t.label} className="btn-ghost text-xs" onClick={() => void createIn(kind, t.code)}>
            + {t.label}
          </button>
        ))}
      </div>
    </section>
  );

  return (
    <div className="min-h-full bg-slate-950">
      <header className="flex items-center justify-between border-b border-slate-800 bg-slate-900/70 px-6 py-3">
        <div className="flex items-center gap-2">
          <Compass className="h-5 w-5 text-archer-400" />
          <span className="font-semibold">Omniapps Archer</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-slate-400">{user?.email}</span>
          <button className="btn-ghost" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl p-6">
        {error && <div className="card mb-4 border-red-900/60 text-red-300">{error}</div>}
        {loading && <p className="text-sm text-slate-400">Loading…</p>}

        <Section
          title="Cloud (Archer)"
          icon={<Cloud className="h-4 w-4 text-archer-400" />}
          items={cloudList}
          kind="cloud"
          emptyHint={user ? 'No cloud projects yet. Create one above.' : 'Sign in to sync projects to the cloud.'}
        />

        <Section
          title="Local folder"
          icon={<HardDrive className="h-4 w-4 text-amber-400" />}
          items={localList}
          kind="local"
          emptyHint={
            localAdapter
              ? 'Folder selected, no projects here yet.'
              : 'Pick a folder to keep projects as JSON files under your control.'
          }
        />
        {!localAdapter && (
          <button className="btn-secondary mb-8" onClick={() => void pickLocalFolder()}>
            <FolderOpen className="h-4 w-4" />
            Choose local folder…
          </button>
        )}

        <Section
          title="Browser (this device)"
          icon={<CloudOff className="h-4 w-4 text-slate-400" />}
          items={browserList}
          kind="browser"
        />
      </div>
    </div>
  );
};

export default ProjectsPage;
